import { useEffect } from 'react';
import { FormControl, MenuItem, Select, Grid } from '@mui/material';
import { useField } from 'formik';
import QuestionLayout from './QuestionLayout';
import TooltipReusable from 'src/components/TooltipReusable';
import { useAppDispatch, useAppSelector } from 'src/utils/hooks';
import { updateChildVisibility } from 'src/store/reducers/riskCheck.reducer';

interface Props {
  question: Question;
  riskIdx: number;
  personIdx: number;
}

export default function ComboboxQuestion({
  question,
  riskIdx,
  personIdx,
}: Props) {
  const dispatch = useAppDispatch();
    const { isColorBlindMode } = useAppSelector((state) => state.config);
  const [field, meta] = useField(
    question.key.replace(/\./g, '&').replace(/\[\]/g, '$')
  );
  useEffect(() => {
    const { value } = field;
    const key = question.key;
    if (question.childQuestionary) {
      let isMatch = false;
      for (const child of question.childQuestionary) {
        const regex1 = new RegExp(child.expChild);
        const regex2 = new RegExp(question.expChild);
        if (regex1.test(value) || regex2.test(value)) {
          isMatch = true;
          break;
        }
      }
      if (value !== undefined && value !== null && value !== '') {
        dispatch(
          updateChildVisibility({
            visible: isMatch,
            value,
            key,
            riskIdx,
            personIdx: personIdx - 1,
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);
  return (
    <QuestionLayout question={question} meta={meta}>
      <FormControl hiddenLabel fullWidth>
        <Select
          {...field}
          value={field.value ?? question.answer.value}
          error={meta.touched && Boolean(meta.error)}
          variant="filled"
          disabled={question.readonly}
          sx={{
            backgroundColor: 'inputFields.bgColor',
            color: 'inputFields.main',
            '.MuiSelect-select': {
              color: 'inputFields.main',
            },
            '.MuiInputBase-root': {
              backgroundColor: 'inputFields.bgColor',
            },
            '.MuiSvgIcon-root': {
              color: 'inputFields.main',
            },
            '& .MuiFilledInput-root:before': {
              borderBottom: (theme) =>
                `1px solid ${theme.palette.inputFields?.main || '#000000'}`,
            },
            '& .MuiFilledInput-root:after': {
              borderBottom: (theme) =>
                `2px solid ${theme.palette.inputFields?.main || '#000000'}`,
            },
            '& .Mui-disabled': {
              backgroundColor: isColorBlindMode
                ? 'transparent !important'
                : 'inputFields.bgColor !important',
              color: 'text.disabled !important',
            },
            '& .MuiInputLabel-root': {
              color: 'inputFields.main',
            },
          }}
        >
          {question.answer.picks?.length &&
            question.answer.picks.map((p, index) => (
              <MenuItem key={`${p.id}${index}`} value={p.id}  sx={{
                  color: 'inputFields.main',
                  backgroundColor: 'inputFields.bgColor',
                  '&:hover': {
                    backgroundColor: isColorBlindMode 
                      ? 'rgba(0, 0, 0, 0.04)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: isColorBlindMode
                      ? 'rgba(0, 0, 0, 0.08)'
                      : 'rgba(0, 0, 0, 0.08)',
                  },
                }}>
                {p.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <Grid item paddingLeft={'20px'}>
        {question.answer.prefilled && !question.readonly && (
          <TooltipReusable
            title={[
              'Diese Angabe wurde aus Ihren vorherigen Angaben übernommen. Bitte überprüfen Sie diese auf Korrektheit.',
            ]}
            iconFlag={false}
          />
        )}
      </Grid>
    </QuestionLayout>
  );
}
